<script setup>
/**
 * The Performance Lab mark: tripla's bird, as large as the box allows, with a
 * small lab flask standing on the same baseline whose liquid is a speed gauge.
 *
 * The bird is tripla's own logo, its three shapes as the logo draws them, in
 * the logo's magentas on light and as the white logo draws it on dark. The
 * flask and the gauge are drawn here, not taken from Google's PageSpeed
 * Insights or Labs marks: their logos inside ours would say Google made or
 * endorses this. The footer's "Powered by Google PageSpeed" is that credit.
 *
 * Colours are the `--mark-*` tokens (styles/tokens.css), so it follows the
 * theme, and paper, which is always light.
 */
import { useId } from 'vue'

/** One gradient per instance: the header can appear twice in a document. */
const liquid = `lab-liquid-${useId()}`
</script>

<template>
  <svg class="ui-labmark" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient :id="liquid" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" class="liquid-top" />
        <stop offset="1" class="liquid-bottom" />
      </linearGradient>
    </defs>
    <!-- tripla's bird, as large as the box allows; its tail is the baseline. -->
    <g transform="translate(1 0.44) scale(0.4545)">
      <path class="tip" d="M48.872 21.867l7.486-13.203 9.634 14.145z" />
      <path class="wing" d="M45.202 52.88L0 59.755l56.358-51.09z" />
      <path class="facet" d="M11.038 15.195l9.407 26.024L45.201 52.88 34.873 28.141z" />
    </g>
    <!-- The flask, small, standing on the bird's baseline under its wing; its
         strokes drawn thicker so they survive the scale. -->
    <g transform="translate(19.6 13.3) scale(0.48)">
      <rect class="glass" x="8" y="6.4" width="11" height="2.4" rx="1.2" />
      <path
        class="glass"
        d="M9.6 8.8V14L3 25.6C2 27.5 3.3 29.8 5.5 29.8H21.5C23.7 29.8 25 27.5 24 25.6L17.4 14V8.8Z"
      />
      <path
        :fill="`url(#${liquid})`"
        d="M6.19 20C9 18.9 11.3 21.2 13.5 20.1C15.8 19 18.2 21.1 20.81 20L24 25.6C25 27.5 23.7 29.8 21.5 29.8H5.5C3.3 29.8 2 27.5 3 25.6Z"
      />
      <path class="gauge" d="M9.3 27.2A4.2 4.2 0 0 1 17.7 27.2" />
      <path class="needle" d="M13.5 27.2L15.1 25.35" />
      <circle class="hub" cx="13.5" cy="27.2" r="1.15" />
    </g>
  </svg>
</template>

<style scoped>
.ui-labmark {
  display: block;
  flex: none;
}
.glass {
  fill: var(--mark-glass);
  stroke: var(--mark-glass-edge);
  stroke-width: 1.5;
  stroke-linejoin: round;
}
.liquid-top {
  stop-color: var(--mark-liquid-top);
}
.liquid-bottom {
  stop-color: var(--mark-liquid-bottom);
}
.gauge,
.needle {
  fill: none;
  stroke: #fff;
  stroke-linecap: round;
}
.gauge {
  stroke-width: 1.8;
}
.needle {
  stroke-width: 1.6;
}
.hub {
  fill: #fff;
}
.wing {
  fill: var(--mark-wing);
}
.facet {
  fill: var(--mark-facet);
}
.tip {
  fill: var(--mark-tip);
}
</style>
